import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChefHat,
  RefreshCw,
  AlertCircle,
  Clock,
  CheckCircle2,
  Play,
  XCircle,
} from 'lucide-react';
import apiService from '../services/api';
import PosCaisseSharedHeader from '../components/caisse/PosCaisseSharedHeader';
import { POS_Z_CLASS } from '../utils/posZLayers';
import { usePosDisplayTimeZone } from '../hooks/usePosDisplayTimeZone';
import { buildCaisseAccueilHref } from '../utils/posCaisseHubNav';
import { getAdminUserFromStorage } from '../utils/posStaffAccess';
import {
  KDS_ALL_STATIONS_ID,
  canViewAllKdsStations,
  groupKdsQueueLines,
  mergeKdsStationQueues,
  resolveInitialKdsStationId,
} from '../utils/kdsQueueUtils';

const POLL_MS = 8_000;
const STATION_STORAGE_PREFIX = 'clickorico_kds_station_';

const STATUS_META = {
  TO_PREPARE: { label: 'À préparer', badge: 'bg-amber-100 text-amber-950 border-amber-300' },
  IN_PREP: { label: 'En préparation', badge: 'bg-sky-100 text-sky-950 border-sky-300' },
  READY: { label: 'Prêt', badge: 'bg-emerald-100 text-emerald-950 border-emerald-300' },
  SERVED: { label: 'Servi', badge: 'bg-slate-100 text-slate-600 border-slate-200' },
  CANCELLED: { label: 'Annulé', badge: 'bg-red-50 text-red-800 border-red-200' },
};

const NEXT_ACTIONS = {
  TO_PREPARE: [{ to: 'IN_PREP', label: 'Démarrer', icon: Play, testId: 'kds-start-prep' }],
  IN_PREP: [{ to: 'READY', label: 'Marquer prêt', icon: CheckCircle2, testId: 'kds-mark-ready' }],
  READY: [],
  SERVED: [],
  CANCELLED: [],
};

function resolveCommercantId(searchParams) {
  const fromUrl = searchParams.get('commercantId');
  if (fromUrl) return fromUrl;
  try {
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    return user.commercantId || user.commercant?.id || null;
  } catch {
    return null;
  }
}

function formatElapsed(sentAt) {
  if (!sentAt) return '—';
  const ms = Date.now() - new Date(sentAt).getTime();
  if (ms < 0) return '0 min';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h} h ${min % 60} min`;
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(
      new Date(iso)
    );
  } catch {
    return '—';
  }
}

function formatOptions(snapshot) {
  if (!snapshot) return null;
  if (typeof snapshot === 'string') return snapshot;
  if (Array.isArray(snapshot)) {
    return snapshot.map((o) => o?.label || o?.nom || JSON.stringify(o)).join(', ');
  }
  if (typeof snapshot === 'object') {
    const parts = [];
    for (const [k, v] of Object.entries(snapshot)) {
      if (v == null || k === 'raw') continue;
      parts.push(`${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`);
    }
    return parts.length ? parts.join(' · ') : null;
  }
  return String(snapshot);
}

export default function CaisseKitchen() {
  const [searchParams] = useSearchParams();
  const commercantId = resolveCommercantId(searchParams);
  const posClock = usePosDisplayTimeZone();
  const posStaffUser = useMemo(() => getAdminUserFromStorage(), []);
  const canViewAll = useMemo(() => canViewAllKdsStations(posStaffUser), [posStaffUser]);
  const caisseAccueilHref = useMemo(
    () => buildCaisseAccueilHref(commercantId),
    [commercantId]
  );
  const [loading, setLoading] = useState(true);
  const [moduleOk, setModuleOk] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState('');
  const [stationsReady, setStationsReady] = useState(false);
  const [stationProductCounts, setStationProductCounts] = useState({});
  const [stationQueueCounts, setStationQueueCounts] = useState({});
  const [lines, setLines] = useState([]);
  const [queueLoaded, setQueueLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLineId, setActionLineId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const showToast = (msg, kind = 'ok') => {
    setToast(kind === 'error' ? `⚠ ${msg}` : msg);
    window.setTimeout(() => setToast(''), 5000);
  };

  const storageKey = commercantId ? `${STATION_STORAGE_PREFIX}${commercantId}` : null;

  const loadMeta = useCallback(async () => {
    if (!commercantId) {
      setError('Aucun commerçant sélectionné.');
      setModuleOk(false);
      return;
    }
    try {
      const com = await apiService.getCommercant(commercantId);
      const enabled =
        Boolean(com?.moduleCaisse) &&
        Boolean(com?.modulePosRestaurant) &&
        Boolean(com?.modulePosRestaurantKitchen);
      setModuleOk(enabled);
      if (!com?.moduleCaisse) {
        setError('Activez le module Caisse (POS).');
      } else if (!com?.modulePosRestaurant) {
        setError('Activez le module Restaurant POS.');
      } else if (!com?.modulePosRestaurantKitchen) {
        setError(
          'Le module Cuisine / préparation n\'est pas activé (Super Admin → Modules).'
        );
      } else {
        setError('');
      }
    } catch (e) {
      setModuleOk(false);
      setError(e.message || 'Impossible de charger le commerçant');
    }
  }, [commercantId]);

  const loadStations = useCallback(async () => {
    setStationsReady(false);
    try {
      const data = await apiService.listKitchenPrepStations(commercantId);
      const list = data?.stations || [];
      setStations(list);
      if (list.length) {
        try {
          const prodRes = await apiService.getProduitsByCommercant(commercantId, false);
          const products = prodRes?.produits || prodRes?.data || prodRes || [];
          const counts = {};
          if (Array.isArray(products)) {
            for (const p of products) {
              const sid = p.preparationStationId;
              if (sid) counts[sid] = (counts[sid] || 0) + 1;
            }
          }
          setStationProductCounts(counts);
        } catch {
          setStationProductCounts({});
        }
      } else {
        setStationProductCounts({});
      }
      if (!stationId && list.length) {
        const pick = resolveInitialKdsStationId({
          stations: list,
          storageKey,
          canViewAll,
        });
        setStationId(pick);
      }
    } finally {
      setStationsReady(true);
    }
  }, [commercantId, stationId, storageKey, canViewAll]);

  const loadQueue = useCallback(
    async (silent = false) => {
      if (!commercantId || !stationId || moduleOk !== true || stations.length === 0) return;
      if (!silent) setRefreshing(true);
      try {
        const stationQueues = await Promise.all(
          stations.map(async (station) => {
            try {
              const data = await apiService.getKitchenStationQueue(station.id, commercantId);
              return { station, lines: data?.lines || [] };
            } catch {
              return { station, lines: [] };
            }
          })
        );
        const { merged, counts } = mergeKdsStationQueues(stationQueues);
        const nextCounts = { ...counts };
        if (canViewAll) {
          nextCounts[KDS_ALL_STATIONS_ID] = merged.length;
        }
        setStationQueueCounts(nextCounts);

        if (stationId === KDS_ALL_STATIONS_ID) {
          setLines(merged);
        } else {
          setLines(stationQueues.find((q) => q.station.id === stationId)?.lines || []);
        }
        setError('');
        setQueueLoaded(true);
      } catch (e) {
        setQueueLoaded(false);
        if (e?.code === 'MODULE_POS_RESTAURANT_KITCHEN_DISABLED' || e?.status === 403) {
          setModuleOk(false);
          setError('Module cuisine inactif pour ce commerçant.');
        } else {
          setError(e.message || 'Erreur de synchronisation');
        }
      } finally {
        if (!silent) setRefreshing(false);
      }
    },
    [commercantId, stationId, moduleOk, stations, canViewAll]
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadMeta();
      setLoading(false);
    })();
  }, [loadMeta]);

  useEffect(() => {
    if (moduleOk !== true) return;
    loadStations().catch((e) => setError(e.message || 'Postes introuvables'));
  }, [moduleOk, loadStations]);

  useEffect(() => {
    if (stationId === KDS_ALL_STATIONS_ID && !canViewAll && stations.length) {
      setStationId(stations[0].id);
    }
  }, [canViewAll, stationId, stations]);

  useEffect(() => {
    if (!stationId || moduleOk !== true) return;
    try {
      if (storageKey) localStorage.setItem(storageKey, stationId);
    } catch {
      /* ignore */
    }
    loadQueue(true);
    const t = window.setInterval(() => loadQueue(true), POLL_MS);
    return () => window.clearInterval(t);
  }, [stationId, moduleOk, loadQueue, storageKey]);

  const viewingAll = stationId === KDS_ALL_STATIONS_ID;
  const groups = useMemo(
    () => groupKdsQueueLines(lines, { byCommande: viewingAll }),
    [lines, viewingAll]
  );
  const activeStation = viewingAll ? null : stations.find((s) => s.id === stationId);
  const queueCountForTab = (tabId) => stationQueueCounts[tabId] ?? 0;

  const runTransition = async (line, toStatus) => {
    setActionLineId(line.id);
    try {
      const res = await apiService.patchKitchenPrepLineStatus(line.id, {
        toStatus,
        expectedVersion: Number(line.version),
        commercantId,
      });
      const updated = res?.line;
      if (updated?.id) {
        setLines((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
      } else {
        await loadQueue(true);
      }
      showToast(
        toStatus === 'SERVED'
          ? 'Service enregistré'
          : toStatus === 'READY'
            ? 'Prêt signalé'
            : 'Statut mis à jour'
      );
    } catch (e) {
      if (e?.code === 'PREP_LINE_VERSION_CONFLICT' || e?.status === 409) {
        showToast('Conflit — un autre terminal a modifié cette ligne. Actualisation…', 'error');
        await loadQueue(true);
      } else if (e?.code === 'PREP_TRANSITION_INVALID') {
        showToast(e.message || 'Transition non autorisée', 'error');
        await loadQueue(true);
      } else {
        showToast(e.message || 'Action impossible', 'error');
      }
    } finally {
      setActionLineId(null);
      setConfirmAction(null);
    }
  };

  const onActionClick = (line, action) => {
    if (action.confirm) {
      setConfirmAction({ line, action });
      return;
    }
    void runTransition(line, action.to);
  };

  if (loading) {
    return (
      <div className="p-6 text-slate-600" data-testid="kds-loading">
        Chargement de l&apos;écran cuisine…
      </div>
    );
  }

  if (moduleOk === false) {
    return (
      <div className="p-6 max-w-lg" data-testid="kds-module-blocked">
        <p className="text-red-700 font-medium">{error || 'Accès cuisine refusé'}</p>
        <Link to={caisseAccueilHref} className="text-emerald-700 underline mt-4 inline-block">
          Retour caisse
        </Link>
      </div>
    );
  }

  const configHref = `/caisse/restaurant?commercantId=${encodeURIComponent(commercantId || '')}&config=prep`;
  const activeProductCount = activeStation ? stationProductCounts[activeStation.id] || 0 : 0;

  return (
    <div className="min-h-[100dvh] w-full min-w-0 bg-slate-900 text-white pb-[max(6rem,env(safe-area-inset-bottom))]" data-testid="kds-root">
      <PosCaisseSharedHeader variant="dark" commercant={posClock.commercant} />
      <header className={`sticky top-0 ${POS_Z_CLASS.pageToolbar} bg-slate-950 border-b border-slate-700 px-4 py-3 flex flex-wrap items-center gap-3 pt-[max(0.75rem,env(safe-area-inset-top))]`}>
        <Link
          to={caisseAccueilHref}
          className="inline-flex items-center gap-2 text-slate-200 min-h-[44px] px-2"
          data-testid="kds-back-caisse"
        >
          <ArrowLeft className="w-5 h-5" aria-hidden />
          Menu caisse
        </Link>
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <ChefHat className="w-6 h-6 text-orange-400" aria-hidden />
          Cuisine / Préparation
        </h1>
        <Link
          to={`/caisse/print?commercantId=${commercantId || ''}`}
          className="text-sm text-orange-300 underline min-h-[44px] inline-flex items-center"
          data-testid="kds-link-print-config"
        >
          Imprimantes
        </Link>
        <button
          type="button"
          data-testid="kds-refresh"
          className="ml-auto min-h-[44px] min-w-[44px] rounded-xl border border-slate-600 bg-slate-800 px-3"
          onClick={() => loadQueue(false)}
          disabled={refreshing}
          aria-label="Actualiser"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </header>

      {toast && (
        <div className="mx-4 mt-3 rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-sm" role="status">
          {toast}
        </div>
      )}
      {error && (
        <div className="mx-4 mt-3 rounded-xl bg-red-950 border border-red-700 px-4 py-3 text-sm flex gap-2">
          <AlertCircle className="w-5 h-5 shrink-0" />
          {error}
        </div>
      )}

      <section className="p-4" data-testid="kds-station-picker">
        <p className="text-sm text-slate-400 mb-2">Poste de travail</p>
        <div className="flex flex-wrap gap-2">
          {canViewAll && stations.length > 0 && (
            <button
              type="button"
              data-testid="kds-station-all"
              className={`min-h-[48px] px-4 rounded-xl border font-medium ${
                stationId === KDS_ALL_STATIONS_ID
                  ? 'bg-orange-500 border-orange-400 text-white'
                  : 'bg-slate-800 border-slate-600 text-slate-100'
              }`}
              onClick={() => setStationId(KDS_ALL_STATIONS_ID)}
            >
              Tout
              <span className="ml-2 text-sm opacity-90">({queueCountForTab(KDS_ALL_STATIONS_ID)})</span>
            </button>
          )}
          {stations.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`kds-station-${s.code}`}
              className={`min-h-[48px] px-4 rounded-xl border font-medium ${
                stationId === s.id
                  ? 'bg-orange-500 border-orange-400 text-white'
                  : 'bg-slate-800 border-slate-600 text-slate-100'
              }`}
              onClick={() => setStationId(s.id)}
            >
              {s.label}
              <span className="ml-2 text-sm opacity-90">({queueCountForTab(s.id)})</span>
            </button>
          ))}
          {stationsReady && stations.length === 0 && (
            <div
              className="w-full rounded-2xl border border-orange-500/40 bg-orange-950/30 p-4 text-sm"
              data-testid="kds-empty-no-stations"
            >
              <p className="font-medium text-orange-100">Aucun poste de préparation configuré</p>
              <p className="text-orange-200/80 mt-1">
                Créez au minimum un poste Cuisine et un poste Bar, puis affectez vos produits.
              </p>
              <Link
                to={configHref}
                className="inline-flex min-h-[44px] items-center mt-3 rounded-xl bg-orange-500 px-4 font-medium text-white"
                data-testid="kds-link-config-stations"
              >
                Configurer les postes
              </Link>
            </div>
          )}
        </div>
        {viewingAll && stations.length > 0 && (
          <p className="text-xs text-slate-500 mt-2" data-testid="kds-station-summary">
            Tous les postes · {groups.length} groupe(s) · {lines.length} ligne(s) active(s)
          </p>
        )}
        {activeStation && (
          <p className="text-xs text-slate-500 mt-2">
            {activeStation.kind} · {groups.length} commande(s) active(s) · {activeProductCount} produit(s)
            affecté(s)
          </p>
        )}
      </section>

      <section
        className="pos-kds-queue-grid p-4 space-y-4 w-full max-w-[100rem] mx-auto overflow-x-hidden"
        data-testid="kds-queue"
      >
        {stationsReady && stations.length > 0 && activeStation && activeProductCount === 0 && (
          <div
            className="rounded-2xl border border-amber-500/40 bg-amber-950/20 p-4 text-sm text-amber-100"
            data-testid="kds-empty-no-products"
          >
            Aucun produit n&apos;est affecté au poste « {activeStation.label} ». Affectez des produits
            dans la configuration restaurant.
            <Link to={configHref} className="block mt-2 underline text-amber-200">
              Ouvrir la configuration
            </Link>
          </div>
        )}
        {stationId && queueLoaded && groups.length === 0 && !error && (viewingAll || activeProductCount > 0) && (
          <p className="text-center text-slate-500 py-16" data-testid="kds-empty-queue">
            {viewingAll
              ? 'Aucune commande à préparer pour le moment.'
              : 'Aucune commande à préparer pour ce poste pour le moment.'}
          </p>
        )}
        {stationId && !queueLoaded && !error && stations.length > 0 && (
          <p className="text-center text-slate-500 py-8 text-sm">Synchronisation de la file…</p>
        )}
        <div className="pos-kds-orders-grid w-full">
        {groups.map((g) => (
          <article
            key={g.key}
            className="rounded-2xl border border-slate-700 bg-slate-800 overflow-hidden min-w-0"
            data-testid={`kds-order-${g.commandeId || g.key}`}
          >
            <div className="px-4 py-3 bg-slate-950/80 flex flex-wrap items-center gap-3 border-b border-slate-700">
              <div>
                <p className="font-bold text-lg">
                  Table {g.tableLabel || '—'}
                  <span className="text-slate-400 font-normal text-sm ml-2">
                    #{String(g.commandeId || '').slice(-6) || '—'}
                  </span>
                </p>
                <p className="text-sm text-slate-400">
                  {g.serviceMode || 'Service'} · envoyé {formatTime(g.sentAt)}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-2 text-orange-300 font-semibold">
                <Clock className="w-5 h-5" />
                {formatElapsed(g.sentAt)}
              </div>
            </div>
            <ul className="divide-y divide-slate-700">
              {g.lines.map((line) => {
                const meta = STATUS_META[line.status] || STATUS_META.TO_PREPARE;
                const actions = (NEXT_ACTIONS[line.status] || []).filter(
                  (a) => line.status !== 'SERVED' && line.status !== 'CANCELLED'
                );
                const opts = formatOptions(line.optionsSnapshot);
                return (
                  <li key={line.id} className="p-4" data-testid={`kds-line-${line.id}`}>
                    <div className="flex flex-wrap gap-3 justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-lg">
                          {line.quantity}× {line.productNameSnapshot}
                          {viewingAll && line.stationLabel && (
                            <span
                              className="ml-2 text-sm font-medium text-orange-200 bg-orange-950/50 border border-orange-700/50 rounded-lg px-2 py-0.5"
                              data-testid="kds-line-station"
                            >
                              {line.stationLabel}
                            </span>
                          )}
                        </p>
                        {opts && <p className="text-sm text-slate-300 mt-1">{opts}</p>}
                        {line.prepNotesSnapshot && (
                          <p className="text-sm text-amber-200 mt-2 bg-amber-950/40 rounded-lg px-3 py-2">
                            {line.prepNotesSnapshot}
                          </p>
                        )}
                        <span
                          className={`inline-block mt-2 text-xs font-medium px-2 py-1 rounded-lg border ${meta.badge}`}
                        >
                          {meta.label}
                        </span>
                      </div>
                      <div className="flex flex-col gap-2 min-w-[140px]">
                        {actions.map((action) => {
                          const Icon = action.icon;
                          return (
                            <button
                              key={action.to}
                              type="button"
                              data-testid={action.testId}
                              data-line-id={line.id}
                              disabled={actionLineId === line.id}
                              className={`min-h-[48px] rounded-xl font-semibold text-sm flex items-center justify-center gap-2 ${
                                action.to === 'SERVED'
                                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                                  : action.to === 'READY'
                                    ? 'bg-sky-600 hover:bg-sky-500 text-white'
                                    : 'bg-orange-600 hover:bg-orange-500 text-white'
                              } disabled:opacity-50`}
                              onClick={() => onActionClick(line, action)}
                            >
                              <Icon className="w-4 h-4" />
                              {action.label}
                            </button>
                          );
                        })}
                        {line.status !== 'SERVED' && line.status !== 'CANCELLED' && (
                          <button
                            type="button"
                            data-testid="kds-cancel-line"
                            disabled={actionLineId === line.id}
                            className="min-h-[44px] rounded-xl border border-red-800 text-red-300 text-sm flex items-center justify-center gap-1"
                            onClick={() =>
                              onActionClick(line, {
                                to: 'CANCELLED',
                                label: 'Annuler',
                                confirm:
                                  'Annuler cette ligne en cuisine ? (historique conservé, pas de sortie stock)',
                              })
                            }
                          >
                            <XCircle className="w-4 h-4" />
                            Annuler
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </article>
        ))}
        </div>
      </section>

      {confirmAction && (
        <div
          className={`fixed inset-0 ${POS_Z_CLASS.modalConfirm} flex items-end sm:items-center justify-center p-4 bg-black/60`}
          role="presentation"
          onClick={() => setConfirmAction(null)}
        >
          <div
            className="bg-white text-slate-900 rounded-2xl w-full max-w-md p-5 space-y-4"
            data-testid="kds-confirm-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-medium">{confirmAction.action.confirm}</p>
            <p className="text-sm text-slate-600">
              {confirmAction.line.quantity}× {confirmAction.line.productNameSnapshot}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 min-h-[48px] rounded-xl border"
                onClick={() => setConfirmAction(null)}
              >
                Retour
              </button>
              <button
                type="button"
                className="flex-1 min-h-[48px] rounded-xl bg-emerald-600 text-white font-semibold"
                data-testid="kds-confirm-action"
                onClick={() => runTransition(confirmAction.line, confirmAction.action.to)}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
